import { useCallback } from 'react'
import useOnEntityCreated from './use-on-entity-created'
import useOnEntityUpdated from './use-on-entity-updated'
import useOnEntityDeleted from './use-on-entity-deleted'

/**
 * Hook to automatically sync table data with entity events
 * 
 * @param {string} entityType - The type of entity (e.g., 'university', 'student')
 * @param {Array} data - Current table data array
 * @param {Function} setData - State setter function for table data
 * @param {Object} pagination - Pagination object with { page, total }
 * @param {Function} setPagination - State setter function for pagination
 * @param {Function} fetchData - Optional function to fetch data if needed (for first page refresh on create)
 * @param {Object} fetchParams - Optional parameters to pass to fetchData
 * @param {Function} getId - Optional function to get ID from entity (default: entity => entity.id)
 * 
 * @example
 * const [universities, setUniversities] = useState([])
 * const [pagination, setPagination] = useState({ page: 1, total: 0 })
 * 
 * useTableDataSync(
 *   'university',
 *   universities,
 *   setUniversities,
 *   pagination,
 *   setPagination,
 *   fetchUniversities,
 *   { page: 1, filters }
 * )
 */
function useTableDataSync(
  entityType,
  data,
  setData,
  pagination,
  setPagination,
  fetchData = null,
  fetchParams = null,
  getId = (entity) => entity?.id
) {
  // Handle entity created event
  useOnEntityCreated(entityType, null, useCallback(({ entityId, entityData }) => {
    if (!entityData || !entityId) return

    const id = getId(entityData)
    if (!id) return

    // If we're on the first page, add the new entity to the list
    if (pagination?.page === 1) {
      setData(prev => {
        // Check if entity already exists (shouldn't happen, but safety check)
        const exists = prev.some(item => getId(item) === id)
        if (exists) return prev

        // Add to beginning of list
        const newList = [entityData, ...prev]

        // Update pagination total
        if (setPagination) {
          setPagination(prevPagination => ({
            ...prevPagination,
            total: (prevPagination?.total || 0) + 1
          }))
        }

        return newList
      })
    } else if (fetchData) {
      // If we're not on first page or entityData is missing, refresh to get updated count
      if (fetchParams) {
        fetchData(1, fetchParams)
      } else {
        fetchData(1)
      }
    }
  }, [entityType, pagination?.page, fetchData, fetchParams, getId, setData, setPagination]))

  // Handle entity updated event - update single row in place
  useOnEntityUpdated(entityType, null, useCallback(({ entityId, entityData }) => {
    if (!entityData || !entityId) return

    // Use entityId directly, or get from entityData if not provided
    const id = entityId || getId(entityData)
    if (!id) return

    setData(prev => {
      const index = prev.findIndex(item => getId(item) === id)
      
      if (index !== -1) {
        // Update the specific row - merge with existing data to preserve all fields
        const newList = [...prev]
        newList[index] = { ...newList[index], ...entityData }
        return newList
      }
      
      return prev
    })
  }, [entityType, getId, setData]))

  // Handle entity deleted event - remove from list
  useOnEntityDeleted(entityType, null, useCallback(({ entityId }) => {
    if (!entityId) return

    setData(prev => {
      const filtered = prev.filter(item => getId(item) !== entityId)

      // Update pagination total
      if (setPagination) {
        setPagination(prevPagination => ({
          ...prevPagination,
          total: Math.max(0, (prevPagination?.total || 0) - 1)
        }))
      }

      return filtered
    })
  }, [entityType, getId, setData, setPagination]))
}

export default useTableDataSync

